import { memo, useMemo, type FC } from "react";
import type { PlaneBounds, PlaneCoordinate, PlaneScreenPoint, ToScreenPoint } from "./CartesianPlane";
import styles from "./DeckBottomViewCard.module.css";
import {
  getBottomViewMeasurementLabels,
  getOffsetMeasurementSegment,
  type JoistMeasurementExample,
  type MeasurementSegment,
} from "./deckBottomViewGeometry";

type BottomMeasurementsLayerProps = {
  bounds: PlaneBounds | null;
  examples: JoistMeasurementExample[];
  exportMode: boolean;
  formatSpacing: (spacingMeters: number | null) => string;
  formatSupportCount: (supportCount: number | null | undefined) => string;
  supportsCountLabel: string;
  toScreenPoint: ToScreenPoint;
};

type MeasurementLine = {
  key: string;
  offsetSegment: MeasurementSegment;
  segment: MeasurementSegment;
};

type ScreenPoint = { pixelX: number; pixelY: number };

type LabelPlacement = { left: number; top: number; transform: string };

// Gap between the line and the label's near edge - not a shift of the label's center, so it clears
// the line regardless of how wide the label text ends up being (a centered offset large enough to
// clear a wide label would look wrong once applied to a short one, and vice versa).
const MEASUREMENT_LABEL_GAP_PX = 8;

// How far (px) an extension line's far end overshoots past the offset dimension line it meets - the
// small "witness line" overshoot conventional in architectural/CAD dimensioning, purely cosmetic so
// it's a fixed screen-space nudge rather than a plane-space one.
const EXTENSION_LINE_OVERSHOOT_PX = 4;

// Extends `to` a few more pixels past `from` along the same direction, for the small overshoot above.
const overshootPoint = (from: ScreenPoint, to: ScreenPoint, overshootPx: number): ScreenPoint => {
  const dx = to.pixelX - from.pixelX;
  const dy = to.pixelY - from.pixelY;
  const length = Math.hypot(dx, dy) || 1;

  return {
    pixelX: to.pixelX + (dx / length) * overshootPx,
    pixelY: to.pixelY + (dy / length) * overshootPx,
  };
};

// Anchors the label's near EDGE (not its center) a fixed gap away from the line/joist it describes,
// on whichever side is farther from the deck's center - same outward convention as
// DeckEdgeMeasurements, but edge-anchored so a wide label can't swing back across a vertical line.
const getLabelPlacement = (
  screenPoint: PlaneScreenPoint,
  point: PlaneCoordinate,
  bounds: PlaneBounds,
  orientation: "horizontal" | "vertical",
  gapPx: number,
): LabelPlacement => {
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  if (orientation === "vertical") {
    const pushRight = point.x >= centerX;

    return {
      left: screenPoint.pixelX + (pushRight ? gapPx : -gapPx),
      top: screenPoint.pixelY,
      transform: `translate(${pushRight ? "0%" : "-100%"}, -50%)`,
    };
  }

  const pushUp = point.y >= centerY;

  return {
    left: screenPoint.pixelX,
    top: screenPoint.pixelY + (pushUp ? -gapPx : gapPx),
    transform: `translate(-50%, ${pushUp ? "-100%" : "0%"})`,
  };
};

// The bottom view's example dimensions (joist and support spacing, support count) with their labels.
const BottomMeasurementsLayer: FC<BottomMeasurementsLayerProps> = ({
  bounds,
  examples,
  exportMode,
  formatSpacing,
  formatSupportCount,
  supportsCountLabel,
  toScreenPoint,
}) => {
  // In plane coordinates, so only rebuilt when the deck or its examples change - not on every zoom/pan.
  const lines = useMemo((): MeasurementLine[] => {
    if (!bounds) {
      return [];
    }

    return examples.flatMap((example) => {
      const exampleLines: MeasurementLine[] = [];

      if (example.joistSpacingSegment && example.joistSpacingMeters != null) {
        exampleLines.push({
          key: `${example.kind}-joist-gap`,
          offsetSegment: getOffsetMeasurementSegment(example.joistSpacingSegment, bounds),
          segment: example.joistSpacingSegment,
        });
      }
      if (example.supportSpacingSegment && example.supportSpacingMeters != null) {
        exampleLines.push({
          key: `${example.kind}-support-gap`,
          offsetSegment: getOffsetMeasurementSegment(example.supportSpacingSegment, bounds),
          segment: example.supportSpacingSegment,
        });
      }

      return exampleLines;
    });
  }, [bounds, examples]);

  // The export capture carries no text at all - the PDF draws these labels itself afterwards, as
  // vector text sized to match its own tables (see getBottomViewMeasurementLabels), so there's
  // nothing for the plane's overflow:hidden to clip regardless of how far a label sits from the deck.
  const labels = useMemo(
    () =>
      exportMode || !bounds
        ? []
        : getBottomViewMeasurementLabels(
            examples,
            bounds,
            formatSpacing,
            (supportCount) => `${supportsCountLabel}: ${formatSupportCount(supportCount)}`,
          ),
    [bounds, examples, exportMode, formatSpacing, formatSupportCount, supportsCountLabel],
  );

  if (!bounds || examples.length === 0) {
    return null;
  }

  return (
    <>
      <svg className={styles.structureLayer} aria-hidden="true">
        {lines.map(({ key, offsetSegment, segment }) => {
          const start = toScreenPoint(segment.start);
          const end = toScreenPoint(segment.end);
          const offsetStart = toScreenPoint(offsetSegment.start);
          const offsetEnd = toScreenPoint(offsetSegment.end);

          if (!start || !end || !offsetStart || !offsetEnd) {
            return null;
          }

          // Architectural-dimension look: an extension line runs from each true reference point out
          // to the offset dimension line (with a small overshoot past it), which then carries the
          // actual measured span alongside the joist/support row instead of running through it.
          const startExtensionEnd = overshootPoint(start, offsetStart, EXTENSION_LINE_OVERSHOOT_PX);
          const endExtensionEnd = overshootPoint(end, offsetEnd, EXTENSION_LINE_OVERSHOOT_PX);

          return (
            <g key={key}>
              <line
                className={styles.measurementLine}
                x1={start.pixelX}
                y1={start.pixelY}
                x2={startExtensionEnd.pixelX}
                y2={startExtensionEnd.pixelY}
              />
              <line
                className={styles.measurementLine}
                x1={end.pixelX}
                y1={end.pixelY}
                x2={endExtensionEnd.pixelX}
                y2={endExtensionEnd.pixelY}
              />
              <line
                className={styles.measurementLine}
                x1={offsetStart.pixelX}
                y1={offsetStart.pixelY}
                x2={offsetEnd.pixelX}
                y2={offsetEnd.pixelY}
              />
            </g>
          );
        })}
      </svg>
      {labels.map(({ key, point, orientation, text }) => {
        const screenPoint = toScreenPoint(point);

        if (!screenPoint) {
          return null;
        }

        const placement = getLabelPlacement(screenPoint, point, bounds, orientation, MEASUREMENT_LABEL_GAP_PX);

        return (
          <div
            key={key}
            className={styles.measurementLabel}
            style={{ left: placement.left, top: placement.top, transform: placement.transform }}
          >
            {text}
          </div>
        );
      })}
    </>
  );
};

// Memoized: re-renders only when the examples, their formatting or the view change.
export default memo(BottomMeasurementsLayer);
